import { useI18nContext } from '@i18n/i18n-react';
import { appColors } from '@base/useTheme';
import { SheetConfigStandardColumn, SheetSortDirection } from '@common/api';
import { ContextMenu, ContextMenuRenderProps } from '@components/context-menu/ContextMenu';
import { ArrowUp, ArrowDown, Checked, Close, Sort, UnChecked } from '@components/icons/FarseerIcons';
import { useLogEvent } from '@providers/TrackingProvider';
import { columnTypeToStandardColumn } from '@util/enums';
import { $toggleInSet } from '@util/patch';
import { useSheetEditorContext } from '../editor/SheetEditorProvider';
import { SheetViewColumn } from '../editor/sheetEditorTableTypes';
import { useSheetEditorSort } from '../editor/useSheetEditorSort';
import { SheetFreezeMenu } from '../context-menu/SheetFreezeMenu';

export const SheetStandardColumnsEditMenu = ({
    column,
    columnIndex,
    ...menuProps
}: {
    columnIndex: number;
    column: SheetViewColumn;
} & ContextMenuRenderProps) => {
    const { LL } = useI18nContext();
    const logEvent = useLogEvent();

    const {
        state: { canEditSheet: canEdit, referencing },
        api: { updateConfig, updateSheet },
    } = useSheetEditorContext();

    const columnType = columnTypeToStandardColumn[column.type];

    const { addRule } = useSheetEditorSort();

    const handleSort = (direction: SheetSortDirection) => {
        addRule(column.key, direction);
    };

    return (
        <ContextMenu {...menuProps} placement="bottom-start">
            <ContextMenu.Item
                item={{
                    icon: Sort,
                    label: LL.SORT(),
                    disabled: referencing.sort,
                }}
            >
                <ContextMenu.Item
                    item={{
                        icon: ArrowUp,
                        label: LL.ASCENDING(),
                        disabled: referencing.sort,
                        action: () => {
                            handleSort(SheetSortDirection.Asc);
                        },
                    }}
                />
                <ContextMenu.Item
                    item={{
                        icon: ArrowDown,
                        label: LL.DESCENDING(),
                        disabled: referencing.sort,
                        action: () => {
                            handleSort(SheetSortDirection.Desc);
                        },
                    }}
                />
            </ContextMenu.Item>
            <ContextMenu.Divider />
            <SheetFreezeMenu columnIndex={columnIndex} />
            {canEdit && (
                <>
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            icon: () => (column.readonly ? <Checked color={appColors.primary.w500} /> : <UnChecked />),
                            label: LL.LOCK_COLUMN(),
                            disabled: referencing.columns || columnType === SheetConfigStandardColumn.Namedescription,
                            action: async () => {
                                await updateSheet({ columns: { standardReadonly: $toggleInSet(columnType) } });
                                logEvent('sheet/#/columns/standard');
                            },
                        }}
                    />
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            icon: Close,
                            label: LL.REMOVE(),
                            disabled: referencing.columns,
                            action: async () => {
                                await updateConfig({ columns: { standard: $toggleInSet(columnType) } });
                                logEvent('sheet/#/columns/standard/delete');
                            },
                        }}
                    />
                </>
            )}
        </ContextMenu>
    );
};
